import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Status } from 'src/components/atoms/status';

import { Scrollbar } from '../../../../components/atoms/scrollbar';

import TableActionCell from '@/components/atoms/table-action-cell';
import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { OrganizationAdmin } from '@/features/organization-admins/api/types/organization-admin';

export interface OrganizationAdminsActionSlotPayload {
  id: number;
  email: string;
}

interface Props {
  data: OrganizationAdmin[];
  isLoading: boolean;
  error: string;
  actionsSlot: (payload: OrganizationAdminsActionSlotPayload) => ReactNode;
}

const OrganizationAdminsTable = ({ data, isLoading, error, actionsSlot }: Props) => {
  const { t } = useTranslation(['organizationAdmins']);

  const columns = [
    {
      id: 'email',
      label: t('organizationAdmins:email'),
    },
    {
      id: 'role',
      label: t('organizationAdmins:role'),
    },
    {
      id: 'button',
      label: '',
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.id}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(({ id, email, isOwner }: OrganizationAdmin) => (
              <TableRow key={id}>
                <TableCell>
                  {email ? (
                    <Typography color="text.primary" variant="inherit">
                      {email}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'primary.main' }}>
                      N/A
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Status
                    label={isOwner ? t('organizationAdmins:owner') : t('organizationAdmins:admin')}
                    color={isOwner ? 'primary.main' : 'warning.main'}
                  />
                </TableCell>
                <TableActionCell>{actionsSlot({ id, email })}</TableActionCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
      <DataStatusIndicator isFetching={isLoading} error={error} dataLength={data?.length} />
    </Box>
  );
};

export default OrganizationAdminsTable;
